
import React, { useState } from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { ListItemButton, Stack, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import DatePickerMui from '../../common/controls/DatePickerMui';
import { isEarlierDate } from '../../../utils/isEarlierDate';
import { daysBetweenDates } from '../../../utils/daysBetweenDates';
import { getInventoryTransactions } from '../../../api/reportService';
import ReportDataGridModal from './ReportDataGridModal';
import InventoryTransactionsReportDataGrid from '../lists/InventoryTransactionsReportDataGrid';
import CategorySelect from '../../inventory/controls/CategorySelect';
import TransactionTypeSelect from '../../inventory/controls/TransactionTypeSelect';
import ProcedureAutocomplete from '../../common/controls/ProcedureAutocomplete';
import SupplierAutocomplete from '../../inventory/controls/SupplierAutocomplete';
import UserAutocomplete from '../../common/controls/UserAutocomplete';

export default function InventoryTransactionsReport({mode = 'list'}) {
  const { t } = useTranslation('common'); 
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSort(['date', 'desc']);
    setDateFrom("");
    setDateTo("");
    setCategoryId("");
    setTypeId("");
    setHistoryEntryTypeId("");
    setSupplierId("");
    setUserId("");
  };

  const { showSnackbar } = useSnackbar();

  const [sort, setSort] = useState(['date', 'desc']);

  const [viewOpen, setViewOpen] = React.useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [reportData, setReportData] = useState(null);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [typeId, setTypeId] = useState("");
  const [historyEntryTypeId, setHistoryEntryTypeId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [userId, setUserId] = useState("");

  const onInvoiceDateFromChange = (date) => {
    setDateFrom(date);
  };

  const onInvoiceDateToChange = (date) => {
    setDateTo(date);
  };

  const fetchData = async () => {
    if (!dateFrom.trim()) {
      showSnackbar(t('reports.messages.date_from_required'), "warning");
      return;
    }
    if (!dateTo.trim()) {
      showSnackbar(t('reports.messages.date_to_required'), "warning");
      return;
    }
    if (!isEarlierDate(dateFrom, dateTo)) {
      showSnackbar(t('reports.messages.date_from_not_earlier'), "warning");
      return;
    }
    if (daysBetweenDates(dateFrom, dateTo) > 365) {
      showSnackbar(t('reports.messages.date_range_too_large'), "warning");
      return;
    }
    try {
      setFetchingData(true)
      const response = await getInventoryTransactions(true, dateFrom, dateTo, typeId, categoryId, historyEntryTypeId, userId, supplierId );
      // console.log(response);
      setReportData(response || null);
      setViewOpen(true);
    } catch (error) {
      console.error("Error fetching items:", error);
    } finally {
      setFetchingData(false);
    }
  }; 

  return (
    <React.Fragment>
      {mode === 'list' ? 
        <ListItem>
          <ListItemButton onClick={handleClickOpen}>
          <ListItemIcon>
            <CalendarMonthIcon fontSize='large' color='secondary'/>
          </ListItemIcon>
          <ListItemText 
            primary="Appointments"
            secondary="generate appointments report"
          />
          </ListItemButton>
        </ListItem>
      : null }
      {mode === 'button' ? 
        <Button variant='outlined' onClick={handleClickOpen}>Transactions</Button>
      : null }

      <Dialog onClose={handleClose} open={open} disableRestoreFocus>

        <DialogTitle>
        <Stack spacing={1}>
          <Typography variant="h6">Inventory Transactions Report</Typography>
        </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <>
            <DatePickerMui
              label='Date From'
              onChange={onInvoiceDateFromChange}
            />
            <DatePickerMui
              label='Date To'
              onChange={onInvoiceDateToChange}
            />
            <CategorySelect
              label='Item Category'
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)} 
            />
            <TransactionTypeSelect
              label="Transaction Type"
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
            />
            {/* <ProcedureAutocomplete
              label="Procedure"
              value={historyEntryTypeId}
              onChange={(id) => setHistoryEntryTypeId(id)} 
            /> */}
            <SupplierAutocomplete
              label="Supplier"
              value={supplierId}
              onChange={(id) => setSupplierId(id)} 
            />
            <UserAutocomplete
              label="Created By"
              value={userId}
              onChange={(id) => setUserId(id)} 
            />
            <br />
            <Button
              variant='outlined'
              fullWidth
              onClick={fetchData}
            >
              Generate Report
            </Button>
          </>
        </DialogContent>

        <DialogActions>
          <div style={{flex: '1 0 0'}} />
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
        <ReportDataGridModal 
          title='Inventory Transactions Report'
          reportData={reportData}
          reportDataGrid={<InventoryTransactionsReportDataGrid data={reportData?.data} />}
          open={viewOpen}
          onClose={() => {
            setViewOpen(false);
          }}
        />
      </Dialog>
    </React.Fragment>
  );
}